import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Compass, CloudSun, Bookmark } from "lucide-react";
import EmailAuth from "./EmailAuth";

const slides = [
  {
    label: "Find spots",
    title: "Trails Ozzy would actually wag for",
    body: "Browse thousands of UK paths and campsites that genuinely welcome dogs — distance-sorted from wherever you are.",
    img: "https://images.unsplash.com/photo-1725367163169-8b7c9e4a0eef?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    Icon: Compass,
  },
  {
    label: "Check the weather",
    title: "A walk score, before you walk",
    body: "Each spot gets a great / ok / avoid score from live forecasts — so you only get muddy paws when you mean to.",
    img: "https://images.unsplash.com/photo-1570700264983-5c2e42475bf1?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    Icon: CloudSun,
  },
  {
    label: "Save & review",
    title: "Your basecamp travels with you",
    body: "Pin favourites, save routes offline, and Scout trails by rating terrain difficulty to help other dogs — even with no signal.",
    img: "https://images.unsplash.com/photo-1687208626339-b808bfe5a93a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    Icon: Bookmark,
  },
];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => { localStorage.setItem("td_onboarded", "in-progress"); }, []);

  const finish = () => {
    localStorage.setItem("td_onboarded", "true");
    navigate("/discover");
  };

  if (step < 3) {
    const s = slides[step];
    const Icon = s.Icon;
    return (
      <div className="min-h-screen flex flex-col">
        <div className="relative flex-1">
          <img
            src={s.img}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading={step === 0 ? "eager" : "lazy"}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-sand via-brand-sand/40 to-transparent" />
          <button
            data-testid="onboarding-skip"
            onClick={finish}
            className="absolute top-6 right-5 text-sm font-medium text-brand-text bg-brand-surface/90 backdrop-blur px-3 py-1.5 rounded-full border border-brand-border"
          >
            Skip
          </button>
        </div>
        <div className="bg-brand-sand px-6 pt-2 pb-10 -mt-32 relative animate-fade-up">
          <div className="td-section-label flex items-center gap-2"><Icon size={14} /> {s.label}</div>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-brand-text mt-3 leading-tight">{s.title}</h1>
          <p className="text-brand-subtle mt-3 leading-relaxed">{s.body}</p>
          <div className="flex items-center justify-between mt-7">
            <div className="flex gap-1.5" data-testid="onboarding-dots">
              {slides.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-8 bg-brand-primary" : "w-4 bg-brand-border"}`} />
              ))}
            </div>
            <button
              data-testid="onboarding-next"
              onClick={() => setStep((x) => x + 1)}
              className="td-btn-primary"
            >
              {step === slides.length - 1 ? "Get Started" : "Next"} <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 4th slide — auth
  return (
    <div className="min-h-screen px-6 py-10 max-w-md mx-auto flex flex-col">
      <button
        data-testid="onboarding-skip-auth"
        onClick={finish}
        className="self-end text-sm font-medium text-brand-subtle hover:text-brand-text"
      >
        Skip for now
      </button>
      <EmailAuth onSignedIn={finish} />
    </div>
  );
}
